import { Annotation as AnnotationV2 } from '@iiif/presentation-2';
import { Annotation as AnnotationV3 } from '@iiif/presentation-3';
import * as Sentry from '@sentry/browser';
import { saveAs } from 'file-saver';
import { IiifProfile, ImageInformationResponse } from 'ol/format/IIIFInfo';
import { v4 as uuid } from 'uuid';
import { Element, ElementCompact, xml2js } from 'xml-js';
import { buildImageRequestUrl } from '@aipb/gulliver/browser/util';

// TODO: move to util/string
export function stripEndingSlash(s: string) {
  return s.replace(/\/$/, '');
}

export function testUrlInDomains(url: string, domains: string[]) {
  if (domains.length === 0) return false;

  return new RegExp('^.*(' + domains.join('|') + ').*').test(url);
}

export function isEmpty(obj) {
  return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;
}

export function isArray(value) {
  return Array.isArray(value);
}

export function isObject(value) {
  if (isArray(value)) return false;

  return typeof value === 'object';
}

export function isUndefined(value): boolean {
  return value === undefined;
}

export function copyToClipboard(s: string): void {
  // Create new element
  const el = document.createElement('textarea');
  // Set value (string to be copied)
  el.value = s;
  // Set non-editable to avoid focus and move outside of view
  el.setAttribute('readonly', '');
  el.setAttribute('style', 'position: absolute;left: -9999px');
  document.body.appendChild(el);
  // Select text inside element
  el.select();
  // Copy text to clipboard
  document.execCommand('copy');
  // Remove temporary element
  document.body.removeChild(el);
}

export function hasKeyRecursive(obj: any, key: string): boolean {
  let found = false;

  for (const i in obj) {
    if (i === key) {
      found = true;
      break;
    }

    const o = obj[i];

    if (isObject(o)) {
      found = hasKeyRecursive(o, key);

      if (found) {
        break;
      }
    }

    if (isArray(o)) {
      for (const j of o) {
        const found = hasKeyRecursive(j, key);

        if (found) {
          return found;
        }
      }
    }
  }

  return found;
}

export function getValueByKeyRecursive(obj: any, key: string): any {
  let found = null;

  for (const i in obj) {
    if (i === key) {
      return obj[key];
    }

    const o = obj[i];

    if (isObject(o)) {
      found = getValueByKeyRecursive(o, key);

      if (found) {
        return found;
      }
    }

    if (isArray(o)) {
      for (const j of o) {
        const found = getValueByKeyRecursive(j, key);

        if (found) {
          return found;
        }
      }
    }
  }

  return null;
}

/**
 * Parsování stringu ve formátu tečkové notace na data objektu
 *
 * Např.
 *
 * key: "messages.form.user_created"
 *
 * data : {
 *      messages : {
 *          form : {
 *              user_created: "User was created successfully."
 *          }
 *      }
 * }
 *
 * parseDotNotation(key, data) => vrátí "User was created successfully."
 *
 * při nenalezení atributů v objektu vrátí key
 */
export function parseDotNotation(key: string, data: Record<never, never>): string {
  return key.split('.').reduce((o, i) => {
    if (typeof o === 'string') {
      return o;
    }
    return i in o ? o[i] : key;
  }, data);
}

export function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function saveFileFromUrl(url: string, filename = uniqueId(), format = 'png'): void {
  saveAs(url, `${filename}.${format}`);
}

export function uniqueId() {
  return uuid();
}

export function parseXML(xml: string): Element | ElementCompact {
  let parsed = null;

  try {
    parsed = xml2js(xml.replace(/&/g, '&amp;'), {
      alwaysChildren: true
    });
  } catch (e) {
    Sentry.captureException(e);
    throw new Error(e);
  }

  return parsed;
}

export function getQuality(info: ImageInformationResponse) {
  const profile = <IiifProfile>info?.['profile']?.[1];

  if (!profile) {
    return 'default';
  }

  const qualities = profile.qualities;

  if (!qualities) {
    return 'default';
  }

  if (qualities.includes('default')) {
    return 'default';
  }

  return qualities[0];
}

export function getIIIFCropUrl(
  image: AnnotationV2 | AnnotationV3,
  info: ImageInformationResponse,
  x: number,
  y: number,
  w: number,
  h: number,
  rotation: number
): string {
  const quality = getQuality(info);

  return buildImageRequestUrl('id' in image ? image.body['service'][0]['id'] : image.resource['service']['@id'], {
    quality,
    region: `${Math.trunc(x)},${Math.trunc(y)},${Math.trunc(w)},${Math.trunc(h)}`,
    rotation
  });
}

export function getAppVersion(app: string): string {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return `${app}@${require('../../../../../../package.json').version}`;
}

export function isNil(value): boolean {
  return value === null || value === undefined;
}

export function cookiesAvailable() {
  let cookieEnabled = navigator.cookieEnabled;

  if (typeof navigator.cookieEnabled == 'undefined' && !cookieEnabled) {
    document.cookie = 'testcookie';
    cookieEnabled = document.cookie.indexOf('testcookie') != -1;
  }
  return cookieEnabled;
}

export function localeStorageAvailable() {
  const storage = window.localStorage;
  try {
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}
